import React, { useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import { 
  Calculator, TrendingUp, Target, Scale, BookOpen, Info,
  Download, Mail, Loader2, ChevronUp, ChevronDown, X, Send,
  CheckCircle2, FileText
} from 'lucide-react';
import { API_URL } from './constants';
import logger from '../../lib/logger';

export const CalculationDetails = ({ results }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', name: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  if (!results || !results.dampening_factors) return null;
  
  const { kpi, financial_summary, dampening_factors, scenario_config, interventions } = results;
  const df = dampening_factors;
  
  const handleExportSummary = async (e) => {
    e.stopPropagation();
    setExporting(true);
    try {
      const response = await authedFetch(`${API_URL}/api/business-case-builder/export-calculation-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          solution_data: results,
          currency: 'USD'
        })
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calculation_summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      logger.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };
  
  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailData.email) return;
    
    setSendingEmail(true);
    try {
      const response = await authedFetch(`${API_URL}/api/business-case-builder/email-calculation-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          recipient_email: emailData.email,
          recipient_name: emailData.name || null,
          personal_message: emailData.message || null,
          solution_data: results,
          currency: 'USD'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }
      
      setEmailSent(true);
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSent(false);
        setEmailData({ email: '', name: '', message: '' });
      }, 2000);
    } catch (error) {
      logger.error('Email error:', error);
      alert(error.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };
  
  return (
    <div className="bg-white rounded-xl border text-gray-900">
      {/* Header - Always visible */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer"
        data-testid="view-calculation-details-btn"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calculator className="text-blue-600" size={20} />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900">View Calculation Details</h4>
            <p className="text-sm text-gray-500">See how ROI, KPI impact, and dampening factors were calculated</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportSummary}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
            data-testid="export-calculation-summary-btn"
          >
            {exporting ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <Download size={14} />
            )}
            <span>Export</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShowEmailModal(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors"
            data-testid="email-calculation-summary-btn"
          >
            <Mail size={14} />
            <span>Share</span>
          </button>
          <span className="text-sm text-blue-600 font-medium">
            {df.combined_adjustment ? `${(df.combined_adjustment * 100).toFixed(0)}% adjustment applied` : ''}
          </span>
          {isExpanded ? <ChevronUp className="text-gray-400" size={20} /> : <ChevronDown className="text-gray-400" size={20} />}
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t p-6 space-y-6">
          {/* ROI Calculation */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <TrendingUp className="text-emerald-600" size={18} />
              ROI Calculation
            </h5>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 font-mono text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Total Investment:</span>
                  <span className="ml-2 font-semibold">${financial_summary.total_investment?.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Financial Impact:</span>
                  <span className="ml-2 font-semibold text-blue-600">${financial_summary.total_financial_impact?.toLocaleString()}</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <span className="text-gray-500">Net Benefit:</span>
                <span className="ml-2 font-semibold">= ${financial_summary.total_financial_impact?.toLocaleString()} - ${financial_summary.total_investment?.toLocaleString()} = </span>
                <span className={`font-bold ${financial_summary.net_benefit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  ${financial_summary.net_benefit?.toLocaleString()}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <span className="text-gray-500">ROI:</span>
                <span className="ml-2">= Net Benefit / Investment = ${financial_summary.net_benefit?.toLocaleString()} / ${financial_summary.total_investment?.toLocaleString()} = </span>
                <span className="font-bold text-emerald-600">{financial_summary.roi_multiplier?.toFixed(2)}x</span>
                <span className="text-gray-500 ml-2">({financial_summary.roi_percent?.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
          
          {/* KPI Impact Calculation */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Target className="text-amber-600" size={18} />
              KPI Impact Calculation
            </h5>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">KPI:</span>
                  <span className="ml-2 font-medium">{kpi?.name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Baseline per Employee:</span>
                  <span className="ml-2 font-semibold">${kpi?.baseline_per_employee?.toLocaleString()}/year</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Target Audience:</span>
                  <span className="ml-2 font-semibold">{financial_summary.total_audience?.toLocaleString()} employees</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Baseline at Risk:</span>
                  <span className="ml-2 font-semibold">${financial_summary.kpi_baseline_total?.toLocaleString()}</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Base KPI Improvement:</span>
                  <span className="font-semibold">{financial_summary.base_kpi_improvement?.toFixed(1)}%</span>
                  <span className="text-gray-400">×</span>
                  <span className="text-gray-500">Combined Adjustment:</span>
                  <span className="font-semibold">{financial_summary.combined_adjustment?.toFixed(1)}%</span>
                  <span className="text-gray-400">=</span>
                  <span className="font-bold text-amber-600">{financial_summary.kpi_uplift_percent?.toFixed(2)}%</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <span className="text-gray-500">Financial Impact:</span>
                <span className="ml-2">= ${financial_summary.kpi_baseline_total?.toLocaleString()} × {financial_summary.kpi_uplift_percent?.toFixed(2)}% = </span>
                <span className="font-bold text-blue-600">${financial_summary.total_financial_impact?.toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          {/* Dampening Factors */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Scale className="text-purple-600" size={18} />
              Dampening Factors Applied
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Scale Dampening */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-700">Scale Dampening</span>
                  <span className="font-bold text-purple-600">{(df.scale_dampening?.factor * 100)?.toFixed(0)}%</span>
                </div>
                <p className="text-xs text-gray-500">{df.scale_dampening?.explanation}</p>
                <p className="text-xs text-gray-400 mt-1">Bracket: {df.scale_dampening?.bracket}</p>
              </div>
              
              {/* Industry Modifier */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-700">Industry Modifier</span>
                  <span className="font-bold text-purple-600">{(df.industry_modifier?.modifier * 100)?.toFixed(0)}%</span>
                </div>
                <p className="text-xs text-gray-500">{df.industry_modifier?.rationale}</p>
                <p className="text-xs text-gray-400 mt-1">Industry: {df.industry_modifier?.industry}</p>
              </div>
              
              {/* Modality Factor */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-700">Learning Modality</span>
                  <span className="font-bold text-purple-600">{(df.modality_factor?.factor * 100)?.toFixed(0)}%</span>
                </div>
                <p className="text-xs text-gray-500">Effectiveness based on delivery method</p>
                <p className="text-xs text-gray-400 mt-1">Primary: {df.modality_factor?.primary_modality}</p>
              </div>
              
              {/* Time Lag */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-700">Time Lag Factor</span>
                  <span className="font-bold text-purple-600">{(df.time_lag?.factor * 100)?.toFixed(0)}%</span>
                </div>
                <p className="text-xs text-gray-500">{df.time_lag?.explanation}</p>
                <p className="text-xs text-gray-400 mt-1">Effective months: {df.time_lag?.effective_months?.toFixed(1)}</p>
              </div>
              
              {/* Capability Transfer */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700">Capability Transfer Rate</span>
                    {df.transfer_method && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        df.transfer_method === 'roe_composite'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {df.transfer_method === 'roe_composite' ? 'Measured' : 'Estimated'}
                      </span>
                    )}
                  </div>
                  <span className={`font-bold ${df.transfer_method === 'roe_composite' ? 'text-emerald-600' : 'text-purple-600'}`}>
                    {df.transfer_method === 'roe_composite'
                      ? `${(df.transfer_rate?.factor * 100)?.toFixed(1)}%`
                      : `${(df.capability_transfer?.rate * 100)?.toFixed(0)}%`
                    }
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {df.transfer_method === 'roe_composite'
                    ? 'ROE Composite: L2 Effectiveness × Motivation × L3 Behavior Transfer'
                    : 'How much learning transfers to job performance'}
                </p>
                {df.transfer_method === 'roe_composite' && df.transfer_rate?.components && (
                  <div className="text-xs text-emerald-600 mt-1 font-mono">
                    L2={df.transfer_rate.components.roe_l2?.score}×{(df.transfer_rate.components.roe_l2?.weight * 100)?.toFixed(0)}% + Mot={df.transfer_rate.components.roe_motivation?.score}×{(df.transfer_rate.components.roe_motivation?.weight * 100)?.toFixed(0)}% + L3={df.transfer_rate.components.roe_l3?.score}×{(df.transfer_rate.components.roe_l3?.weight * 100)?.toFixed(0)}%
                  </div>
                )}
                {df.transfer_method !== 'roe_composite' && (
                  <p className="text-xs text-gray-400 mt-1">
                    Maturity: {df.capability_transfer?.maturity}% | Manager: {df.capability_transfer?.manager_support}
                  </p>
                )}
              </div>
              
              {/* Execution Risk */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-700">Execution Risk Factors</span>
                  <span className="font-bold text-purple-600">{(df.execution_risk?.factor * 100)?.toFixed(0)}%</span>
                </div>
                <p className="text-xs text-gray-500">Training effectiveness × Transfer environment × Measurement</p>
                <p className="text-xs text-gray-400 mt-1">
                  {(df.execution_risk?.training_effectiveness * 100)?.toFixed(0)}% × {(df.execution_risk?.transfer_environment * 100)?.toFixed(0)}% × {(df.execution_risk?.measurement_precision * 100)?.toFixed(0)}%
                </p>
              </div>
            </div>
            
            {/* Combined Adjustment */}
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-purple-900">Combined Adjustment Factor</span>
                  <p className="text-sm text-purple-700 mt-1">
                    All dampening factors multiplied together
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-3xl font-bold text-purple-600">{(df.combined_adjustment * 100)?.toFixed(1)}%</span>
                  <p className="text-xs text-purple-500 mt-1">of theoretical impact realized</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-purple-200 text-xs text-purple-600 font-mono">
                {(df.scale_dampening?.factor * 100)?.toFixed(0)}% × {(df.industry_modifier?.modifier * 100)?.toFixed(0)}% × {(df.modality_factor?.factor * 100)?.toFixed(0)}% × {(df.time_lag?.factor * 100)?.toFixed(0)}% × {(df.capability_transfer?.rate * 100)?.toFixed(0)}% × {(df.execution_risk?.factor * 100)?.toFixed(0)}% = <strong>{(df.combined_adjustment * 100)?.toFixed(1)}%</strong>
              </div>
            </div>
          </div>
          
          {/* Intervention Effectiveness */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <BookOpen className="text-blue-600" size={18} />
              Intervention Effectiveness Breakdown
            </h5>
            <div className="bg-gray-50 rounded-lg p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2">Intervention</th>
                    <th className="pb-2">Modality</th>
                    <th className="pb-2 text-right">KPI Alignment</th>
                    <th className="pb-2 text-right">Effectiveness</th>
                    <th className="pb-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {interventions?.map((int, idx) => (
                    <tr key={int.name || `int-${idx}`} className="border-b border-gray-100">
                      <td className="py-2 font-medium">{int.intervention_name}</td>
                      <td className="py-2 text-gray-600">{int.modality_name}</td>
                      <td className="py-2 text-right text-amber-600">{(int.kpi_alignment * 100)?.toFixed(0)}%</td>
                      <td className="py-2 text-right text-emerald-600">{(int.modality_effectiveness * 100)?.toFixed(0)}%</td>
                      <td className="py-2 text-right">${int.total_cost?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Scenario Configuration */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Info className="text-gray-600" size={18} />
              Scenario Configuration
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Time Horizon</p>
                <p className="text-lg font-bold">{scenario_config?.time_horizon_months} months</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Implementation Lag</p>
                <p className="text-lg font-bold">{scenario_config?.implementation_lag_months} months</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Ramp-up Period</p>
                <p className="text-lg font-bold">{scenario_config?.ramp_up_months} months</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Organization Size</p>
                <p className="text-lg font-bold">{scenario_config?.headcount?.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEmailModal(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="text-white" size={22} />
                <h3 className="text-lg font-semibold text-white">Share Calculation Summary</h3>
              </div>
              <button 
                onClick={() => setShowEmailModal(false)} 
                className="text-white/80 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Content */}
            {emailSent ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="text-emerald-600" size={32} />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Email Sent!</h4>
                <p className="text-gray-500">The calculation summary has been sent successfully.</p>
              </div>
            ) : (
              <form onSubmit={handleSendEmail} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Email <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={emailData.email}
                    onChange={e => setEmailData({ ...emailData, email: e.target.value })}
                    placeholder="stakeholder@company.com"
                    className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    data-testid="email-recipient-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Name <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={emailData.name}
                    onChange={e => setEmailData({ ...emailData, name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    data-testid="email-recipient-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personal Message <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    value={emailData.message}
                    onChange={e => setEmailData({ ...emailData, message: e.target.value })}
                    placeholder="Hi, I wanted to share this ROI analysis with you..."
                    rows={3}
                    className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                    data-testid="email-message-input"
                  />
                </div>
                
                <div className="bg-teal-50 rounded-lg p-3 text-sm text-teal-700">
                  <p className="flex items-center gap-2">
                    <FileText size={16} />
                    A one-page PDF summary will be attached to the email
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowEmailModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingEmail || !emailData.email}
                    className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="send-email-btn"
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
